import { useEffect, useState, useContext } from "react";
import {
    Box, Button, CssBaseline, Grid, Link, Paper, TextField, Typography,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from "@material-ui/core";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from "react-router-dom";
import history from '../../../routes/history';
import useStyles from './style';
import { Context } from "../../../providers/AuthProvider/contexts/AuthContext";
import { HeaderImage, ImageLeft, ImageTopMobile } from "../../Login";
import { useMediaQuery } from "react-responsive";
import { handleRecaptcha, RECAPTCHA_ACTIONS } from "../../../utils/reCaptcha";

type IRouteParams = {
    resetPasswordCode: string;
};

const Copyright = () => (
    <Typography variant="body2" color="textSecondary" align="center">
        {'Copyright © '}
        <Link color="inherit" href="https://www.ijc.org.br/">
            Instituto Jô Clemente
        </Link>{' '}
        {new Date().getFullYear()}
        {'.'}
    </Typography>
);

const FormResetPassWord = ({
    password,
    confirmPassword,
    setPassword,
    setConfirmPassword,
    openConfirmReset,
    setOpenConfirmReset,
    handleSubmitReset,
    isSendResetForm,
    InformationFormResetPass,
    classes
}) => {
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');

    // Função de submit ajustada para validar visualmente
    const handleSubmit = () => {
        let hasError = false;

        if (!password) {
            setPasswordError('Obrigatório');
            hasError = true;
        } else {
            setPasswordError('');
        }

        if (!confirmPassword) {
            setConfirmPasswordError('Obrigatório');
            hasError = true;
        } else {
            setConfirmPasswordError('');
        }

        if (password && confirmPassword && password !== confirmPassword) {
            setConfirmPasswordError('As senhas não conferem');
            hasError = true;
        }

        if (hasError) return;

        // Chamada da API
        handleSubmitReset();
    };

    return (
        <>
            {isSendResetForm ? (
                <InformationFormResetPass />
            ) : (
                <form className={classes.form}>
                    <Typography style={{ fontSize: '15px', fontWeight: 'bold', color: '#5f5f5f' }}>
                        Nova Senha
                    </Typography>
                    <TextField
                        size="small"
                        variant="outlined"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={Boolean(passwordError)}
                        helperText={passwordError}
                    />

                    <Box mt={1} mb={2}>
                        <Typography style={{ fontSize: '15px', fontWeight: 'bold', color: '#5f5f5f' }}>
                            Confirmar Senha
                        </Typography>
                        <TextField
                            size="small"
                            variant="outlined"
                            type="password"
                            fullWidth
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={Boolean(confirmPasswordError)}
                            helperText={confirmPasswordError}
                        />
                    </Box>

                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        className={classes.submit}
                        onClick={() => setOpenConfirmReset(true)}
                    >
                        Enviar
                    </Button>

                    <Dialog open={openConfirmReset} onClose={() => setOpenConfirmReset(false)}>
                        <DialogTitle>Confirmar redefinição</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Deseja realmente redefinir sua senha com os dados informados?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenConfirmReset(false)} color="secondary">
                                Cancelar
                            </Button>
                            <Button onClick={() => { setOpenConfirmReset(false); handleSubmit(); }} color="primary">
                                Confirmar
                            </Button>
                        </DialogActions>
                    </Dialog>
                </form>
            )}
        </>
    );
};


const ResetPassword = () => {
    const { auth } = useContext(Context);
    const classes = useStyles();
    const [isSendResetPass, setIsSendResetPass] = useState(false);
    const [isSuccessResetPass, setIsSuccessResetPass] = useState(false);
    const [renderResetPass, setRenderResetPass] = useState(false);
    const { resetPasswordCode } = useParams<IRouteParams>();
    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 598px)' });

    // Estados de confirmação
    const [openConfirm, setOpenConfirm] = useState(false);
    const [openConfirmReset, setOpenConfirmReset] = useState(false);

    // Estados de redefinição
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSendResetForm, setIsSendResetForm] = useState(false);
    const [isSuccessResetForm, setIsSuccessResetForm] = useState(false);

    useEffect(() => {
        handleRecaptcha(RECAPTCHA_ACTIONS.LOGIN);
        if (resetPasswordCode) setRenderResetPass(true);
    }, [resetPasswordCode]);

    const formik = useFormik({
        initialValues: { email: '', loginType: '2' },
        validationSchema: Yup.object({
            email: Yup.string().email('E-mail no formato inválido.').required('Obrigatório'),
        }),
        onSubmit: (values) => {
            auth?.handleResetPassword(values, RECAPTCHA_ACTIONS.SUBMIT_FORM_RESET).then((ret: boolean) => {
                setIsSendResetPass(true);
                setIsSuccessResetPass(true);
            });
        },
    });

    const InformationSendResetPass = () => (
        <Box m={5}>
            <Typography variant='h6'>
                Se este e-mail estiver cadastrado, enviaremos um e-mail para você com o link para redefinição da senha.
            </Typography>
        </Box>
    );

    const InformationFormResetPass = () => (
        <Box m={5}>
            {isSuccessResetForm ? (
                <Typography variant='h6'>Senha alterada com sucesso.</Typography>
            ) : (
                <>
                    <Typography variant='h6'>Não foi possível criar a nova senha...</Typography>
                    <Button
                        fullWidth
                        onClick={() => { setRenderResetPass(false); history.push('/nova-senha-cliente'); }}
                        variant="text"
                        color="secondary"
                        className={classes.submit}
                    >
                        Tentar novamente
                    </Button>
                </>
            )}
        </Box>
    );

    const handleSubmitReset = () => {
        if (password.length < 6) {
            alert('A senha deve ter 6 ou mais caracteres');
            return;
        }
        if (password !== confirmPassword) {
            alert('As senhas não conferem');
            return;
        }

        auth?.handleNewPassword(
            { resetPasswordCode: resetPasswordCode, newPassword: password },
            RECAPTCHA_ACTIONS.RESET_PASSWORD_CONFIRMATION
        ).then((ret: boolean) => {
            setIsSendResetForm(true);
            setIsSuccessResetForm(true);
        });
    };

    return (
        <Grid container component="main" className={classes.root}>
            <CssBaseline />
            {isTabletOrMobile && <ImageTopMobile />}
            {!isTabletOrMobile && <ImageLeft />}

            <Grid item xs={12} md={7} component={Paper} square>
                <div className={classes.paper}>
                    <Link
                        style={{ width: '100%', padding: '10px', fontWeight: 'bold' }}
                        href="#"
                        variant="body2"
                        onClick={() => history.push('/login/clientes')}
                    >
                        Voltar
                    </Link>

                    <div className={classes.form}>
                        <HeaderImage />
                    </div>

                    {isSendResetPass ? (
                        <InformationSendResetPass />
                    ) : renderResetPass ? (
                        <FormResetPassWord
                            password={password}
                            confirmPassword={confirmPassword}
                            setPassword={setPassword}
                            setConfirmPassword={setConfirmPassword}
                            openConfirmReset={openConfirmReset}
                            setOpenConfirmReset={setOpenConfirmReset}
                            handleSubmitReset={handleSubmitReset}
                            isSendResetForm={isSendResetForm}
                            InformationFormResetPass={InformationFormResetPass}
                            classes={classes}
                        />
                    ) : (
                        <form onSubmit={formik.handleSubmit} className={classes.form}>
                            <Box mb={2}>
                                <Typography variant="h4" style={{ fontWeight: 'bolder' }} color="textPrimary">
                                    Recuperação de senha
                                </Typography>
                                <Typography variant="inherit" style={{ fontSize: '18px' }} color="textSecondary">
                                    Enviaremos um e-mail com o link para a redefinição da senha.
                                </Typography>
                            </Box>

                            <TextField
                                size="small"
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="Email"
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helperText={formik.touched.email && formik.errors.email}
                                {...formik.getFieldProps('email')}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                className={classes.submit}
                                onClick={() => setOpenConfirm(true)}
                            >
                                Enviar
                            </Button>
                        </form>
                    )}

                    <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                        <DialogTitle>Confirmar envio</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Deseja realmente enviar o link de redefinição de senha para o e-mail informado?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenConfirm(false)} color="secondary">
                                Cancelar
                            </Button>
                            <Button
                                onClick={() => {
                                    setOpenConfirm(false);
                                    formik.handleSubmit();
                                }}
                                color="primary"
                            >
                                Confirmar
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Box mt={5}>
                        <Copyright />
                    </Box>
                </div>
            </Grid>
        </Grid>
    );
};

export default ResetPassword;
